import { useFocusRing } from '@react-aria/focus';
import { useTableColumnHeader, useTableSelectAllCheckbox } from '@react-aria/table';
import { mergeProps } from '@react-aria/utils';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { TableState } from '@react-stately/table';
import { GridNode } from '@react-types/grid';
import React, { useImperativeHandle, useRef } from 'react';

import { cx } from 'class-variance-authority';
import Checkbox from './Checkbox';
import { columnHeaderClassNames } from './styles';

interface Props<T> {
    column: GridNode<T>;
    state: TableState<T>;
}

type NativeAttrs = Omit<React.HTMLAttributes<unknown>, keyof Props<any>>;

export type TableSelectAllCheckboxProps<T = unknown> = Props<T> & NativeAttrs;

const TableSelectAllCheckbox = React.forwardRef<HTMLTableCellElement, TableSelectAllCheckboxProps>(
    ({ column, state, ...props }, ref: React.Ref<HTMLTableCellElement | null>) => {
        const tableCellRef = useRef<HTMLTableCellElement | null>(null);

        useImperativeHandle(ref, () => tableCellRef?.current);

        const isSingleSelectionMode = state.selectionManager.selectionMode === 'single';

        const {
            columnHeaderProps
        }: {
            columnHeaderProps: Omit<React.HTMLAttributes<unknown>, keyof TableSelectAllCheckboxProps<unknown>>;
        } = useTableColumnHeader({ node: column }, state, tableCellRef);

        const { checkboxProps } = useTableSelectAllCheckbox(state);
        const { isFocusVisible, focusProps } = useFocusRing();

        return (
            <th
                className={columnHeaderClassNames({ className: cx(props.className, 'tw-w-[55px]'), isFocusVisible })}
                ref={tableCellRef}
                {...mergeProps(columnHeaderProps, focusProps, props)}
            >
                {isSingleSelectionMode ? (
                    <VisuallyHidden>{checkboxProps['aria-label']}</VisuallyHidden>
                ) : (
                    <Checkbox {...checkboxProps} />
                )}
            </th>
        );
    }
);

TableSelectAllCheckbox.displayName = 'TableSelectAllCheckbox';

export default TableSelectAllCheckbox;
